import {
  createInvitationSchema,
  formatDate,
  formatDateTime,
  INVITABLE_ROLES,
  type Invitation,
  type Member,
  type Role,
  Roles,
  routes,
} from '@pmai/contracts';
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  FormDialog,
  IconButton,
  InlineSelect,
  PageHeader,
  SelectField,
  StatusBadge,
  TextField,
} from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiMutation, useApiQuery } from '../../shared/api/hooks';
import { Can, useMe } from '../../shared/auth/session';
import { applyFieldErrors, bind, useZodForm } from '../../shared/forms/form';
import { useEnumLabel, useErrorText } from '../../shared/i18n';
import { useConfirm } from '../../shared/use-confirm';

/** Members, roles and invitations of the active workspace (FR-WS-002, FR-WS-003). */
export function MembersPage() {
  const { t } = useTranslation();
  const me = useMe();
  const errorText = useErrorText();
  const roleLabel = useEnumLabel();
  const canManage = me.permissions.includes('members.manage');
  const isOwner = me.workspace?.role === 'owner';

  const members = useApiQuery(routes.members.list);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const changeRole = useApiMutation(routes.members.changeRole, {
    invalidates: [routes.members.list, routes.auth.me],
  });
  const remove = useConfirm(routes.members.remove, {
    invalidates: [routes.members.list],
    params: (m: Member) => ({ userId: m.userId }),
  });

  const roleOptions = Roles.options
    .filter((o) => isOwner || o.code !== 'owner')
    .map((o) => ({ value: o.code, label: roleLabel(o) }));

  const columns = [
    {
      key: 'name',
      header: t('workspace.members.name'),
      cell: (m: Member) => (
        <span className="font-medium">
          {m.displayName}
          {m.userId === me.user.id && (
            <span className="ml-1.5 text-ink-muted">({t('workspace.members.you')})</span>
          )}
        </span>
      ),
    },
    { key: 'email', header: t('workspace.members.email'), cell: (m: Member) => m.email },
    {
      key: 'role',
      header: t('workspace.members.role'),
      cell: (m: Member) =>
        canManage && m.userId !== me.user.id && (isOwner || m.role !== 'owner') ? (
          <InlineSelect
            label={t('workspace.members.changeRole', { name: m.displayName })}
            value={m.role}
            options={roleOptions}
            onChange={(e) =>
              changeRole.mutate({ params: { userId: m.userId }, body: { role: e.target.value as Role } })
            }
          />
        ) : (
          <StatusBadge tone={Roles.byCode(m.role).tone} label={roleLabel(Roles.byCode(m.role))} />
        ),
    },
    { key: 'joined', header: t('workspace.members.joined'), cell: (m: Member) => formatDate(m.joinedAt) },
    {
      key: 'actions',
      header: '',
      align: 'right' as const,
      cell: (m: Member) =>
        canManage &&
        m.userId !== me.user.id &&
        (isOwner || m.role !== 'owner') && (
          <IconButton
            icon="trash-2"
            label={`${t('workspace.members.remove')}: ${m.displayName}`}
            onClick={() => remove.ask(m)}
          />
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('workspace.members.title')}
        actions={
          <Can perm="members.manage">
            <Button icon="plus" onClick={() => setInviting(true)}>
              {t('workspace.invitations.invite')}
            </Button>
          </Can>
        }
      />
      <div className="flex flex-col gap-6">
        {notice && <Alert tone="success">{notice}</Alert>}
        {changeRole.error && <Alert tone="danger">{errorText(changeRole.error)}</Alert>}
        <DataTable
          label={t('workspace.members.title')}
          columns={columns}
          rows={members.data}
          rowKey={(m) => m.userId}
          loading={members.isPending}
          empty={<EmptyState icon="users" title={t('workspace.members.empty')} />}
        />
        <Can perm="members.manage">
          <InvitationsCard />
        </Can>
      </div>

      <InviteDialog
        open={inviting}
        onClose={() => setInviting(false)}
        onSent={(email) => {
          setInviting(false);
          setNotice(t('workspace.invitations.sent', { email }));
        }}
      />
      <ConfirmDialog
        {...remove.dialog}
        title={t('workspace.members.removeTitle')}
        message={t('workspace.members.removeText', { name: remove.target?.displayName ?? '' })}
        confirmLabel={t('workspace.members.remove')}
      />
    </>
  );
}

function InvitationsCard() {
  const { t } = useTranslation();
  const roleLabel = useEnumLabel();
  const invitations = useApiQuery(routes.invitations.list);
  const revoke = useConfirm(routes.invitations.revoke, {
    invalidates: [routes.invitations.list],
    params: (i: Invitation) => ({ id: i.id }),
  });

  return (
    <Card title={t('workspace.invitations.title')}>
      <DataTable
        label={t('workspace.invitations.title')}
        rows={invitations.data}
        rowKey={(i) => i.id}
        loading={invitations.isPending}
        empty={<p className="text-sm text-ink-muted">{t('workspace.invitations.empty')}</p>}
        columns={[
          { key: 'email', header: t('workspace.members.email'), cell: (i) => i.email },
          {
            key: 'role',
            header: t('workspace.members.role'),
            cell: (i) => (
              <StatusBadge tone={Roles.byCode(i.role).tone} label={roleLabel(Roles.byCode(i.role))} />
            ),
          },
          {
            key: 'expires',
            header: t('workspace.invitations.expires'),
            cell: (i) => formatDateTime(i.expiresAt),
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            cell: (i) => (
              <Button variant="ghost" size="sm" onClick={() => revoke.ask(i)}>
                {t('workspace.invitations.revoke')}
              </Button>
            ),
          },
        ]}
      />
      <ConfirmDialog
        {...revoke.dialog}
        title={t('workspace.invitations.revokeTitle')}
        message={t('workspace.invitations.revokeText', { email: revoke.target?.email ?? '' })}
        confirmLabel={t('workspace.invitations.revoke')}
      />
    </Card>
  );
}

function InviteDialog({
  open,
  onClose,
  onSent,
}: {
  open: boolean;
  onClose: () => void;
  onSent: (email: string) => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const roleLabel = useEnumLabel();
  const form = useZodForm(createInvitationSchema, { defaultValues: { email: '', role: 'member' } });
  const invite = useApiMutation(routes.invitations.create, {
    invalidates: [routes.invitations.list],
    onError: (error) => applyFieldErrors(form, error),
  });
  const close = () => {
    form.reset();
    invite.reset();
    onClose();
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={(o) => !o && close()}
      title={t('workspace.invitations.inviteTitle')}
      submitLabel={t('workspace.invitations.send')}
      submitting={invite.isPending}
      error={invite.error && errorText(invite.error, { CONFLICT: t('workspace.invitations.alreadyMember') })}
      onSubmit={form.handleSubmit((body) =>
        invite.mutate(
          { body },
          {
            onSuccess: () => {
              form.reset();
              onSent(body.email);
            },
          },
        ),
      )}
    >
      <TextField label={t('workspace.members.email')} type="email" required {...bind(form, 'email')} />
      <SelectField
        label={t('workspace.members.role')}
        required
        options={INVITABLE_ROLES.map((code) => ({ value: code, label: roleLabel(Roles.byCode(code)) }))}
        {...bind(form, 'role')}
      />
    </FormDialog>
  );
}
